/**
 * The app's view of the consent state that `public/index.html` establishes
 * before React boots. ADR 0025, second amendment.
 *
 * Deliberately thin. The questions "is this visitor in a region that must be
 * asked" and "what did they answer last time" are settled in index.html,
 * because the answers decide whether a tag loads at all and that decision has
 * to be taken before any of this bundle exists. Re-deriving either here would
 * create a second copy of the region list, and the two would drift the way
 * `isToolRoute`/`isLinkable` are documented to (CLAUDE.md) - silently.
 *
 * So this module reads `window.__mathpagesConsent` and owns only the part the
 * page genuinely owns: writing a new answer, and starting analytics when that
 * answer is yes.
 *
 * When the object is missing entirely - a Jest run, a test that never loaded
 * the template - the app behaves as if no consent were required and shows no
 * banner. That is the same thing that happens off the canonical host today,
 * and it is the correct default for the one environment it applies to: there
 * is no analytics to gate.
 */

export type ConsentChoice = 'granted' | 'denied';

export interface ConsentBridge {
    key: string;
    version: number;
    required: boolean;
    choice: ConsentChoice | null;
    start: () => void;
}

interface ConsentWindow extends Window {
    __mathpagesConsent?: ConsentBridge;
}

function bridge(): ConsentBridge | undefined {
    if (typeof window === 'undefined') {
        return undefined;
    }
    return (window as ConsentWindow).__mathpagesConsent;
}

/** Whether this visitor is in a region that has to be asked before GA loads. */
export function consentRequired(): boolean {
    return bridge()?.required ?? false;
}

/** The stored answer, or null if this visitor has not answered the current version. */
export function consentChoice(): ConsentChoice | null {
    return bridge()?.choice ?? null;
}

/**
 * Record an answer and act on it.
 *
 * Accepting starts GA immediately rather than on the next page load. A visitor
 * who says yes and is then not counted until they navigate is a gap that would
 * show up in the numbers as sampling noise and never be traced back here.
 *
 * Declining does not unload a tag, because under the hard gate there is none to
 * unload - nothing was loaded while the answer was unknown. That is the whole
 * benefit of not using Consent Mode (amendment 2 §B).
 *
 * The write is guarded like every other localStorage write in the app: it
 * throws in a private window and with site data blocked, and a consent banner
 * that breaks the page for someone who blocks storage would be a special kind
 * of failure. The in-memory answer still updates, so the banner still closes
 * and the session still behaves as asked - it is only the memory of it that is
 * lost, and the question gets asked again next time.
 */
export function recordConsent(choice: ConsentChoice): void {
    const state = bridge();
    if (!state) {
        return;
    }

    state.choice = choice;

    try {
        window.localStorage.setItem(
            state.key,
            JSON.stringify({ choice, version: state.version, decidedAt: Date.now() })
        );
    } catch {
        // See the header: the session is still honoured, only the record is lost.
    }

    if (choice === 'granted') {
        state.start();
    }
}
